import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { Plus, Edit3, Trash2, Star, Package, Heart, ShoppingBag, Bell, BellOff, CreditCard, CheckCircle2, AlertCircle, ExternalLink, Lock, Download, ShieldX, Handshake, Eye, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { haptic } from "@/lib/utils";

const fireFirstSaleConfetti = () => {
  const colors = ["#A4D955", "#FFE600", "#FF4500", "#ffffff"];
  confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors });
  setTimeout(() => confetti({ particleCount: 90, spread: 120, origin: { y: 0.4 }, colors }), 400);
  setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { x: 0.2, y: 0.5 }, colors }), 800);
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { subscribePush, permission } = useNotifications() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [myProducts, setMyProducts] = useState([]);
  const [orders, setOrders] = useState({ bought: [], sold: [] });
  const [wishlist, setWishlist] = useState([]);
  const [tab, setTab] = useState("listings");
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get(`/products?seller_id=${user.user_id}&status=`).then(r => setMyProducts(r.data)).catch(()=>{});
    api.get("/orders/my").then(r => {
      setOrders(r.data);
      if (r.data.celebrate_first_sale) {
        fireFirstSaleConfetti();
        haptic("success");
        toast.success("Félicitations pour ta première vente ! 🎉", { duration: 6000 });
        api.post("/orders/first-sale-celebrated").catch(() => {});
      }
    }).catch(()=>{});
    api.get("/wishlist").then(r => setWishlist(r.data)).catch(()=>{});
    api.get("/connect/status").then(r => setConnect(r.data)).catch(()=>{});
  }, [user]);

  // Refresh connect status when returning from Stripe onboarding
  useEffect(() => {
    const cs = searchParams.get("connect");
    if (cs === "done" || cs === "refresh") {
      api.get("/connect/status").then(r => {
        setConnect(r.data);
        if (r.data.onboarded) toast.success("Compte Stripe activé ✓ Tu peux recevoir des paiements !");
        else if (cs === "refresh") toast.info("Onboarding interrompu — reprends quand tu veux");
      }).catch(()=>{});
    }
  }, [searchParams]);

  // Deep link depuis /supprimer-mon-compte : ouvre directement la modale de suppression
  useEffect(() => {
    if (searchParams.get("action") === "delete") setDelOpen(true);
  }, [searchParams]);

  const startConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const r = await api.post("/connect/onboard", {});
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe Connect");
      setConnectLoading(false);
    }
  };

  const openConnectDashboard = async () => {
    setConnectLoading(true);
    try {
      const r = await api.post("/connect/dashboard-link", {});
      window.open(r.data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setConnectLoading(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await api.delete(`/products/${id}`); setMyProducts(myProducts.filter(p => p.product_id !== id)); toast.success("Annonce supprimée"); }
    catch { toast.error("Erreur"); }
  };

  const enablePush = async () => {
    try {
      const res = await subscribePush();
      if (res.ok) toast.success("Notifications activées 🔔");
      else if (res.reason === "denied") toast.error("Active les notifs dans les réglages (Réglages iOS > Fripri ou ton navigateur)");
      else if (res.reason === "timeout" || res.reason === "native-error") toast.error("L'enregistrement des notifications n'a pas abouti — réessaie après la prochaine mise à jour de l'app");
      else toast.error("Pas supporté");
    } catch (e) { toast.error("Erreur"); }
  };

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdNew2, setPwdNew2] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delLoading, setDelLoading] = useState(false);

  const exportData = async () => {
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fripri-mes-donnees-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé ✓");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteAccount = async () => {
    if (delConfirm.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error("Tape SUPPRIMER pour confirmer");
      return;
    }
    setDelLoading(true);
    try {
      const r = await api.delete("/me", { data: { confirmation: delConfirm } });
      toast.success("Compte supprimé. À bientôt 🌱");
      // Force logout
      try { localStorage.removeItem("fripri_logged_in"); localStorage.removeItem("auth_token"); } catch (e) { /* noop */ }
      setTimeout(() => { window.location.href = "/"; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setDelLoading(false);
    }
  };
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdNew !== pwdNew2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (pwdNew.length < 8) { toast.error("Minimum 8 caractères"); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdCurrent, new_password: pwdNew });
      toast.success("Mot de passe mis à jour ✓");
      setPwdOpen(false);
      setPwdCurrent(""); setPwdNew(""); setPwdNew2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setPwdSaving(false); }
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  const activeCount = myProducts.filter(p => p.status==="active").length;
  const soldCount = myProducts.filter(p => p.status==="sold").length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-black text-white flex items-center justify-center font-black text-2xl">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "U").toUpperCase()}
          </div>
          <div>
            <p className="label">Mon espace</p>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tighter">@{user.name?.split(" ")[0]?.toLowerCase() || "moi"}</h1>
            <div className="flex items-center gap-1 mt-1">
              <Star size={12} fill="#FFE600" strokeWidth={0}/>
              <span className="text-xs font-medium">{user.rating?.toFixed(1) || "Nouveau"} {user.review_count ? `· ${user.review_count} avis` : ""}</span>
            </div>
          </div>
        </div>
        <Link to="/vendre" className="btn btn-mint" data-testid="new-listing-btn">
          <Plus size={16} strokeWidth={3}/> Vendre
        </Link>
      </div>

      {/* Push permission banner */}
      {permission !== "granted" && permission !== "denied" && (
        <div className="card border-2 border-black bg-[color:var(--color-yellow)] p-4 mb-6 flex items-center gap-4 card-hover" data-testid="push-banner">
          <Bell size={28} strokeWidth={2.5} />
          <div className="flex-1">
            <p className="font-black tracking-tight">Active les notifs</p>
            <p className="text-xs text-black/80">Sois averti dès qu'un article part ou qu'un message arrive.</p>
          </div>
          <button onClick={enablePush} className="btn btn-primary btn-sm" data-testid="enable-push-btn">Activer</button>
        </div>
      )}

      {/* Stripe Connect — Compte vendeur */}
      <div className={`card border-2 border-black p-4 lg:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 ${connect?.onboarded ? "bg-[color:var(--color-mint)]" : "bg-white"}`} data-testid="connect-banner">
        <div className="w-14 h-14 shrink-0 border-2 border-black flex items-center justify-center bg-white">
          {connect?.onboarded ? <CheckCircle2 size={26} strokeWidth={2.5}/> : <CreditCard size={26} strokeWidth={2.5}/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="label">Compte vendeur · Stripe Connect</p>
          {connect?.onboarded ? (
            <>
              <p className="font-black tracking-tight">Paiements activés ✓</p>
              <p className="text-xs text-black/80">Tes ventes sont reversées automatiquement sur ton compte bancaire — 100% du prix de tes articles, 0% de commission 💚</p>
            </>
          ) : connect?.stripe_account_id ? (
            <>
              <p className="font-black tracking-tight flex items-center gap-2"><AlertCircle size={16}/> Onboarding incomplet</p>
              <p className="text-xs text-[color:var(--color-text-muted)]">Termine la vérification Stripe pour recevoir tes paiements.</p>
            </>
          ) : (
            <>
              <p className="font-black tracking-tight">Active tes versements</p>
              <p className="text-xs text-[color:var(--color-text-muted)]">5 minutes de KYC + RIB pour recevoir l'argent de tes ventes directement sur ton compte bancaire.</p>
            </>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {connect?.onboarded ? (
            <button onClick={openConnectDashboard} disabled={connectLoading} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white" data-testid="connect-dashboard-btn">
              Tableau Stripe <ExternalLink size={12} strokeWidth={3}/>
            </button>
          ) : (
            <button onClick={startConnectOnboarding} disabled={connectLoading} className="btn btn-primary btn-sm" data-testid="connect-onboard-btn">
              {connectLoading ? "Chargement…" : connect?.stripe_account_id ? "Reprendre" : "Activer Stripe"}
            </button>
          )}
        </div>
      </div>

      {/* Agent FRIPRI / Parrainage */}
      <ReferralCard user={user} />

      {/* Sécurité — Changer mot de passe */}
      <div className="card border-2 border-black bg-white p-4 lg:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="security-banner">
        <div className="w-14 h-14 shrink-0 border-2 border-black flex items-center justify-center bg-[color:var(--color-yellow)]">
          <Lock size={26} strokeWidth={2.5}/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="label">Sécurité du compte</p>
          <p className="font-black tracking-tight">Mot de passe</p>
          <p className="text-xs text-[color:var(--color-text-muted)]">Change-le régulièrement pour protéger ton compte Fripri.</p>
        </div>
        <button onClick={() => setPwdOpen(true)} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white shrink-0" data-testid="change-password-btn">
          Modifier
        </button>
      </div>

      {/* Modal changer mot de passe */}
      {pwdOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPwdOpen(false)} data-testid="password-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={changePassword} className="bg-white border-2 border-black w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black tracking-tighter">Nouveau mot de passe</h3>
              <button type="button" onClick={() => setPwdOpen(false)} className="text-2xl leading-none" data-testid="close-pwd-modal">×</button>
            </div>
            <div>
              <label className="label block mb-1">Mot de passe actuel</label>
              <input type="password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} required autoComplete="current-password" className="input w-full" data-testid="current-pwd-input"/>
            </div>
            <div>
              <label className="label block mb-1">Nouveau mot de passe (8 caractères min.)</label>
              <input type="password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} required minLength={8} autoComplete="new-password" className="input w-full" data-testid="new-pwd-input"/>
            </div>
            <div>
              <label className="label block mb-1">Confirme le nouveau mot de passe</label>
              <input type="password" value={pwdNew2} onChange={(e) => setPwdNew2(e.target.value)} required minLength={8} autoComplete="new-password" className="input w-full" data-testid="new-pwd-input-2"/>
            </div>
            <button type="submit" disabled={pwdSaving} className="btn btn-primary w-full" data-testid="submit-pwd-btn">
              {pwdSaving ? "Mise à jour…" : "Mettre à jour"}
            </button>
          </form>
        </div>
      )}

      {/* RGPD — Mes données */}
      <div className="card border-2 border-black bg-white p-4 lg:p-5 mb-6" data-testid="rgpd-card">
        <div className="flex items-start gap-4 mb-3">
          <div className="w-14 h-14 shrink-0 border-2 border-black flex items-center justify-center bg-[color:var(--color-mint)]">
            <ShieldX size={26} strokeWidth={2.5}/>
          </div>
          <div className="flex-1">
            <p className="label">Mes données · RGPD</p>
            <p className="font-black tracking-tight">Tes droits sur tes données personnelles</p>
            <p className="text-xs text-[color:var(--color-text-muted)]">Article 17 (oubli) · Article 20 (portabilité) du RGPD.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <button onClick={exportData} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white flex-1" data-testid="export-data-btn">
            <Download size={14} strokeWidth={3}/> Télécharger mes données (JSON)
          </button>
          <button onClick={() => setDelOpen(true)} className="btn btn-sm border-2 border-[color:var(--color-coral)] text-[color:var(--color-coral)] hover:bg-[color:var(--color-coral)] hover:text-white flex-1" data-testid="delete-account-btn">
            <Trash2 size={14} strokeWidth={3}/> Supprimer mon compte
          </button>
        </div>
      </div>

      {/* Modal suppression de compte */}
      {delOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !delLoading && setDelOpen(false)} data-testid="delete-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-[color:var(--color-coral)] w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-black tracking-tighter text-[color:var(--color-coral)]">Supprimer mon compte</h3>
              <button type="button" onClick={() => setDelOpen(false)} className="text-2xl leading-none" disabled={delLoading} data-testid="close-del-modal">×</button>
            </div>
            <div className="text-sm space-y-2">
              <p><strong>Cette action est irréversible.</strong> Ton compte et ton contenu seront supprimés ou anonymisés selon le RGPD :</p>
              <ul className="text-xs list-disc list-inside text-[color:var(--color-text-muted)] space-y-1">
                <li>Tes annonces actives seront supprimées</li>
                <li>Ton nom et email seront anonymisés</li>
                <li>Tes messages seront effacés</li>
                <li>L&apos;historique de tes commandes est conservé 10 ans (obligation comptable)</li>
              </ul>
            </div>
            <div>
              <label className="label block mb-1">Tape <strong>SUPPRIMER</strong> pour confirmer</label>
              <input
                value={delConfirm}
                onChange={(e) => setDelConfirm(e.target.value)}
                placeholder="SUPPRIMER"
                className="input w-full font-mono"
                disabled={delLoading}
                data-testid="del-confirm-input"
              />
            </div>
            <button
              onClick={deleteAccount}
              disabled={delLoading || delConfirm.trim().toUpperCase() !== "SUPPRIMER"}
              className="btn w-full bg-[color:var(--color-coral)] text-white border-2 border-[color:var(--color-coral)] hover:bg-black hover:border-black disabled:opacity-40"
              data-testid="confirm-delete-btn"
            >
              {delLoading ? "Suppression en cours…" : "Confirmer la suppression définitive"}
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat label="En ligne" value={activeCount} icon={<Package size={18} strokeWidth={2.5}/>} color="bg-white"/>
        <Stat label="Vendus" value={soldCount} icon={<Star size={18} strokeWidth={2.5}/>} color="bg-[color:var(--color-mint)]"/>
        <Stat label="Achats" value={orders.bought.length} icon={<ShoppingBag size={18} strokeWidth={2.5}/>} color="bg-white"/>
        <Stat label="Favoris" value={wishlist.length} icon={<Heart size={18} strokeWidth={2.5}/>} color="bg-[color:var(--color-coral)] text-white"/>
      </div>

      {/* Bannière Fripri Pro */}
      {!user?.is_pro && (
        <Link to="/pro" className="flex items-center gap-3 border-2 border-black bg-[color:var(--color-mint)] p-4 mb-8 card-hover" data-testid="pro-banner">
          <BadgeCheck size={24} strokeWidth={2.5} />
          <div className="flex-1">
            <p className="font-black tracking-tight">Passez en vendeur PRO 💼</p>
            <p className="text-xs">Création de votre entreprise offerte + badge de confiance. Découvrir →</p>
          </div>
        </Link>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar -mx-4 px-4">
        {[
          { k: "listings", label: "Mes annonces" },
          { k: "sold", label: "Mes ventes" },
          { k: "purchases", label: "Mes achats" },
          { k: "wishlist", label: "Favoris" },
        ].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)} className={`btn btn-sm shrink-0 ${tab===t.k ? 'btn-primary' : 'btn-ghost'}`} data-testid={`tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === "listings" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {myProducts.length === 0 ? (
            <div className="lg:col-span-2 text-center py-16 border-2 border-dashed border-black">
              <p className="text-2xl font-black mb-3">Aucune annonce 🫥</p>
              <p className="text-sm text-[color:var(--color-text-muted)] mb-6">Sors un article de ton placard.</p>
              <Link to="/vendre" className="btn btn-mint">+ Créer ma première annonce</Link>
            </div>
          ) : myProducts.map(p => (
            <div key={p.product_id} className="flex gap-3 p-3 border-2 border-black bg-white card-hover">
              <Link to={`/produit/${p.product_id}`} className="w-24 h-24 shrink-0 overflow-hidden bg-[color:var(--color-surface)]">
                {p.images?.[0] && <img src={fileUrl(p.images[0])} className="w-full h-full object-cover"/>}
              </Link>
              <div className="flex-1 min-w-0">
                <p className="label">{p.brand || "—"}</p>
                <h3 className="font-bold truncate">{p.title}</h3>
                <p className="font-black mt-1" style={{color: "var(--color-coral)"}}>{p.price?.toFixed(0)}€</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className={`pill inline-block ${p.status==='active' ? 'pill-mint' : p.status==='sold' ? 'pill-coral' : ''}`}>{p.status === "active" ? "En ligne" : p.status === "sold" ? "Vendu" : p.status}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--color-text-muted)]" data-testid={`views-${p.product_id}`}>
                    <Eye size={12} strokeWidth={2.5}/> {p.views || 0} vue{(p.views || 0) > 1 ? "s" : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--color-text-muted)]">
                    <Heart size={12} strokeWidth={2.5}/> {p.favorites_count || 0}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={()=>navigate(`/vendre?edit=${p.product_id}`)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white" data-testid={`edit-${p.product_id}`}><Edit3 size={14}/></button>
                <button onClick={()=>deleteProduct(p.product_id)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white hover:border-[color:var(--color-coral)]" data-testid={`delete-${p.product_id}`}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "sold" && (
        <div>
          {orders.sold.length === 0 ? <p className="text-center py-12 text-[color:var(--color-text-muted)]">Aucune vente pour l'instant.</p> :
            <div className="space-y-3">{orders.sold.map(o => <OrderRow key={o.order_id} order={o} role="seller"/>)}</div>
          }
        </div>
      )}

      {tab === "purchases" && (
        <div>
          {orders.bought.length === 0 ? <p className="text-center py-12 text-[color:var(--color-text-muted)]">Aucun achat pour l'instant.</p> :
            <div className="space-y-3">{orders.bought.map(o => <OrderRow key={o.order_id} order={o} role="buyer"/>)}</div>
          }
        </div>
      )}

      {tab === "wishlist" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {wishlist.length === 0 ? <p className="col-span-full text-center py-12 text-[color:var(--color-text-muted)]">Aucun favori.</p> :
            wishlist.map(p => (
              <Link key={p.product_id} to={`/produit/${p.product_id}`} className="block product-card">
                <div className="aspect-square overflow-hidden product-img-wrap mb-2">
                  {p.images?.[0] && <img src={fileUrl(p.images[0])} className="product-img w-full h-full object-cover"/>}
                </div>
                <p className="label">{p.brand}</p>
                <h3 className="font-bold text-sm truncate">{p.title}</h3>
                <p className="font-black" style={{color:"var(--color-coral)"}}>{p.price?.toFixed(0)}€</p>
              </Link>
            ))
          }
        </div>
      )}
    </div>
  );
}

function ReferralCard({ user }) {
  const { checkAuth } = useAuth();
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  if (user.role === "agent") {
    return (
      <Link to="/agent" className="card border-2 border-black bg-black text-white p-4 lg:p-5 mb-6 flex items-center gap-4 card-hover" data-testid="agent-space-card">
        <div className="w-14 h-14 shrink-0 border-2 border-[color:var(--color-mint)] flex items-center justify-center bg-[color:var(--color-mint)]">
          <Handshake size={26} strokeWidth={2.5} className="text-black"/>
        </div>
        <div className="flex-1 min-w-0">
          <p className="label text-[color:var(--color-mint)]">Agent FRIPRI · Dépt {user.agent_department}</p>
          <p className="font-black tracking-tight">Mon espace agent</p>
          <p className="text-xs text-white/70">Code : <strong>{user.referral_code}</strong> · Suis tes filleuls et tes commissions 3%.</p>
        </div>
        <span className="btn btn-mint btn-sm shrink-0">Ouvrir →</span>
      </Link>
    );
  }

  if (user.referred_by_agent_id) {
    return (
      <div className="card border-2 border-black bg-white p-4 lg:p-5 mb-6 flex items-center gap-4" data-testid="referred-card">
        <div className="w-14 h-14 shrink-0 border-2 border-black flex items-center justify-center bg-[color:var(--color-mint)]">
          <Handshake size={26} strokeWidth={2.5}/>
        </div>
        <div className="flex-1">
          <p className="label">Accompagnement FRIPRI</p>
          <p className="font-black tracking-tight">Tu es accompagné par un agent ✓</p>
          <p className="text-xs text-[color:var(--color-text-muted)]">Ton agent t'aide à vendre. 3% de tes ventes lui sont reversés.</p>
        </div>
      </div>
    );
  }

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/referral/apply", { code: code.trim() });
      toast.success(`Tu es maintenant accompagné par ${r.data.agent_name} (dépt ${r.data.department}) 🤝`);
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={apply} className="card border-2 border-black bg-white p-4 lg:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="referral-apply-card">
      <div className="w-14 h-14 shrink-0 border-2 border-black flex items-center justify-center bg-white">
        <Handshake size={26} strokeWidth={2.5}/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="label">Agent FRIPRI</p>
        <p className="font-black tracking-tight">Un agent t'accompagne ?</p>
        <p className="text-xs text-[color:var(--color-text-muted)]">Saisis son code : il t'aidera à vendre (3% reversés sur tes ventes).</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <input value={code} onChange={e=>setCode(e.target.value)} placeholder="FR75-A1B2" className="field w-36" data-testid="referral-code-input"/>
        <button type="submit" disabled={saving || !code.trim()} className="btn btn-primary btn-sm disabled:opacity-40" data-testid="referral-apply-btn">
          {saving ? "…" : "Valider"}
        </button>
      </div>
    </form>
  );
}

function Stat({ label, value, icon, color="bg-white" }) {
  return (
    <div className={`p-4 border-2 border-black ${color}`}>
      <div className="flex items-center justify-between mb-2">{icon}<span className="label">{label}</span></div>
      <div className="display-num text-3xl font-black">{value}</div>
    </div>
  );
}

function OrderRow({ order, role }) {
  return (
    <Link to={`/commandes/${order.order_id}`} className="p-4 border-2 border-black bg-white flex items-center justify-between gap-4 card-hover" data-testid={`order-row-${order.order_id}`}>
      <div>
        <p className="label">{role==="seller"?"Vendu":"Acheté"} · {new Date(order.created_at).toLocaleDateString("fr-FR")}</p>
        <p className="font-bold">Commande #{order.order_id.slice(-6).toUpperCase()}</p>
        <p className="text-xs text-[color:var(--color-text-muted)]">{order.product_ids.length} article(s)</p>
      </div>
      <div className="text-right">
        <p className="display-num text-2xl font-black" style={{color:"var(--color-coral)"}}>{order.amount?.toFixed(0)}€</p>
        <span className={`pill ${order.status === 'disputed' ? 'pill-coral' : order.status === 'released' ? 'pill-mint' : ''}`}>{order.status}</span>
      </div>
    </Link>
  );
}
